import { useCallback, useMemo, useState } from 'react';

import { Group, Tabs } from '@mantine/core';

import { PatientTabType } from '../types';

import CareTeamDetails from './CareTeamDetails';
import MedicalInfoDetails from './MedicalInfoDetails';
import SettingsSection from './SettingsSection';
import styles from './TabsSection.module.scss';

interface TabConfig {
  value: PatientTabType;
  label: string;
  component: React.ComponentType<{ patientId: string }>;
}

interface TabsSectionProps {
  patientId: string;
}

const TabsSection = ({ patientId }: TabsSectionProps) => {
  const [selectedTab, setSelectedTab] = useState<PatientTabType>(PatientTabType.CareTeam);

  const tabConfigs = useMemo<TabConfig[]>(
    () => [
      {
        value: PatientTabType.CareTeam,
        label: 'Care Team',
        component: CareTeamDetails
      },
      {
        value: PatientTabType.PatientSettings,
        label: 'Patient Settings',
        component: SettingsSection
      },
      {
        value: PatientTabType.MedicalInfo,
        label: 'Medical Info',
        component: MedicalInfoDetails
      }
    ],
    []
  );

  const handleTabChange = useCallback((value: string | null) => {
    if (value) {
      setSelectedTab(value as PatientTabType);
    }
  }, []);

  const renderTab = useCallback(
    ({ value, label }: TabConfig) => (
      <Tabs.Tab key={value} value={value} className={styles.tab}>
        <Group gap={4}>{label}</Group>
      </Tabs.Tab>
    ),
    []
  );

  const renderTabPanel = useCallback(
    ({ value, component: Component }: TabConfig) => (
      <Tabs.Panel key={value} value={value} className={styles.panel}>
        <div className={styles.content}>
          <Component patientId={patientId} />
        </div>
      </Tabs.Panel>
    ),
    [patientId]
  );

  return (
    <div className={styles.tabsSection}>
      <Tabs variant="none" value={selectedTab} onChange={handleTabChange} className={styles.tabs}>
        <Tabs.List>{tabConfigs.map(renderTab)}</Tabs.List>
        {tabConfigs.map(renderTabPanel)}
      </Tabs>
    </div>
  );
};

export default TabsSection;
